"use strict";
/**
 * Money layer — SPEC §1 (currency), §4.1 (cross-cutting rules), §11 (data integrity).
 *
 * Every monetary value in the platform is an integer number of **millidinars** (md):
 *
 *     1 IQD = 1000 md
 *
 * Stored as `BIGINT` in Postgres, carried as `bigint` in TypeScript, mirrored as
 * `long` in .NET / Kotlin. Floats are never used for money anywhere.
 *
 * Rules encoded here:
 *  - Proration stays exact through the whole computation (one division, at the end).
 *  - The venue's rounding rule is applied **once**, when a session/bill closes —
 *    never during intermediate math (see {@link applyRounding}).
 *  - Rounding targets a whole IQD, i.e. a multiple of 1000 md, because the pilot
 *    venue does not handle sub-dinar cash.
 *
 * Reference reality check (incumbent system): a 0:50 session at 10,000 IQD/hr bills
 * 8,333 IQD. See `money.test.ts`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ROUNDING_RULES = exports.ZERO_MD = exports.MS_PER_HOUR = exports.MD_PER_IQD = void 0;
exports.fromIqd = fromIqd;
exports.fromMillidinars = fromMillidinars;
exports.isMillidinars = isMillidinars;
exports.serializeMd = serializeMd;
exports.toIqd = toIqd;
exports.toIqdDecimalString = toIqdDecimalString;
exports.add = add;
exports.sum = sum;
exports.subtract = subtract;
exports.negate = negate;
exports.abs = abs;
exports.multiply = multiply;
exports.scale = scale;
exports.percentage = percentage;
exports.prorate = prorate;
exports.prorateBetween = prorateBetween;
exports.compare = compare;
exports.isZero = isZero;
exports.isNegative = isNegative;
exports.isPositive = isPositive;
exports.min = min;
exports.max = max;
exports.clampToZero = clampToZero;
exports.isRoundingRule = isRoundingRule;
exports.applyRounding = applyRounding;
exports.roundingStepMd = roundingStepMd;
exports.roundCheckoutTotal = roundCheckoutTotal;
exports.isWholeIqd = isWholeIqd;
exports.formatIqd = formatIqd;
/** Millidinars per whole IQD. */
exports.MD_PER_IQD = 1000n;
/** Milliseconds in one hour — the proration denominator. */
exports.MS_PER_HOUR = 3600000n;
/** Zero money. */
exports.ZERO_MD = 0n;
/** Largest IQD magnitude accepted by {@link fromIqd} (float-safe). */
const MAX_SAFE_IQD = Math.floor(Number.MAX_SAFE_INTEGER / 1000);
/** Internal: brand a plain bigint. */
const brand = (value) => value;
/* -------------------------------------------------------------------------- */
/* Constructors & conversions                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Build money from a whole (or fractional) IQD amount — for config, seeds,
 * price inputs and tests. `fromIqd(5000)` is 5,000 IQD = 5,000,000 md.
 *
 * Fractional IQD is accepted and resolved to the nearest millidinar
 * (`fromIqd(0.5)` -> 500 md); anything finer than 0.001 IQD is rounded
 * half away from zero.
 *
 * @throws TypeError if `iqd` is not a finite number.
 * @throws RangeError if `iqd` exceeds the float-safe range.
 */
function fromIqd(iqd) {
    if (typeof iqd !== 'number' || !Number.isFinite(iqd)) {
        throw new TypeError(`fromIqd expects a finite number, received ${String(iqd)}`);
    }
    if (Math.abs(iqd) > MAX_SAFE_IQD) {
        throw new RangeError(`fromIqd: ${iqd} IQD exceeds the float-safe range (+/-${MAX_SAFE_IQD})`);
    }
    const scaled = iqd * 1000;
    const rounded = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
    return brand(BigInt(rounded));
}
/**
 * Build money from a raw millidinar value: a `bigint` from Prisma, a `number`
 * from a legacy payload, or a `string` from JSON (JSON has no bigint, so the
 * API serialises money as a decimal string — see {@link serializeMd}).
 *
 * @throws TypeError for non-integral input.
 */
function fromMillidinars(value) {
    if (typeof value === 'bigint')
        return brand(value);
    if (typeof value === 'number') {
        if (!Number.isSafeInteger(value)) {
            throw new TypeError(`fromMillidinars expects a safe integer, received ${value}`);
        }
        return brand(BigInt(value));
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!/^[+-]?\d+$/.test(trimmed)) {
            throw new TypeError(`fromMillidinars expects an integer string, received "${value}"`);
        }
        return brand(BigInt(trimmed));
    }
    throw new TypeError(`fromMillidinars: unsupported value type ${typeof value}`);
}
/** Runtime guard. Money is a `bigint` at runtime; the brand is compile-time only. */
function isMillidinars(value) {
    return typeof value === 'bigint';
}
/**
 * Millidinars as a JSON-safe decimal string. Use this on every API boundary —
 * `JSON.stringify` throws on `bigint`.
 */
function serializeMd(amount) {
    return amount.toString(10);
}
/**
 * Approximate IQD as a JS `number`, for charts and percentages only.
 * Never feed the result back into billing math.
 */
function toIqd(amount) {
    return Number(amount) / 1000;
}
/**
 * Exact decimal-IQD string, e.g. `8333333n -> "8333.333"`. Lossless for any
 * magnitude; used by {@link formatIqd} and safe for receipts.
 */
function toIqdDecimalString(amount, decimals = 3) {
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 3) {
        throw new RangeError(`toIqdDecimalString: decimals must be an integer in 0..3`);
    }
    const negative = amount < 0n;
    const magnitude = negative ? -amount : amount;
    const whole = magnitude / exports.MD_PER_IQD;
    const fraction = (magnitude % exports.MD_PER_IQD).toString(10).padStart(3, '0').slice(0, decimals);
    const sign = negative ? '-' : '';
    return decimals === 0 ? `${sign}${whole}` : `${sign}${whole}.${fraction}`;
}
/* -------------------------------------------------------------------------- */
/* Internal exact-division primitives                                         */
/* -------------------------------------------------------------------------- */
/** Integer division rounding halves away from zero. Exact, no floats. */
function divRoundHalfAwayFromZero(numerator, denominator) {
    if (denominator === 0n)
        throw new RangeError('division by zero');
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = n / d;
    const remainder = n % d;
    const magnitude = remainder * 2n >= d ? quotient + 1n : quotient;
    return negative ? -magnitude : magnitude;
}
/** Largest multiple of `step` that is <= `value` (floor toward -Infinity). */
function floorToStep(value, step) {
    const quotient = value / step;
    const remainder = value % step;
    return (remainder !== 0n && value < 0n ? quotient - 1n : quotient) * step;
}
/** Smallest multiple of `step` that is >= `value` (ceiling toward +Infinity). */
function ceilToStep(value, step) {
    const quotient = value / step;
    const remainder = value % step;
    return (remainder !== 0n && value > 0n ? quotient + 1n : quotient) * step;
}
/** Nearest multiple of `step`, halves away from zero (commercial rounding). */
function roundToStep(value, step) {
    return divRoundHalfAwayFromZero(value, step) * step;
}
/* -------------------------------------------------------------------------- */
/* Arithmetic                                                                 */
/* -------------------------------------------------------------------------- */
/** a + b */
function add(a, b) {
    return brand(a + b);
}
/** Sum of any iterable of amounts. Exact — bigint addition never drifts. */
function sum(amounts) {
    let total = 0n;
    for (const amount of amounts)
        total += amount;
    return brand(total);
}
/** a - b (may be negative: refunds, negative sales, balance adjustments). */
function subtract(a, b) {
    return brand(a - b);
}
/** -a */
function negate(a) {
    return brand(-a);
}
/** |a| */
function abs(a) {
    return brand(a < 0n ? -a : a);
}
/**
 * Multiply by an integer scalar — quantities, round counts, print copies.
 * Exact, no rounding involved.
 *
 * For non-integer factors use {@link scale} (exact rational) or
 * {@link percentage}; a float scalar is rejected rather than silently rounded.
 */
function multiply(amount, scalar) {
    if (typeof scalar === 'bigint')
        return brand(amount * scalar);
    if (!Number.isSafeInteger(scalar)) {
        throw new TypeError(`multiply expects an integer scalar (use scale() or percentage() for fractions), received ${scalar}`);
    }
    return brand(amount * BigInt(scalar));
}
/**
 * Exact rational scaling: `amount * numerator / denominator`, with a single
 * division at the end (halves away from zero). The building block for every
 * proportional computation — proration, splits, cashback tiers.
 */
function scale(amount, numerator, denominator) {
    const n = toBigIntExact(numerator, 'scale numerator');
    const d = toBigIntExact(denominator, 'scale denominator');
    if (d === 0n)
        throw new RangeError('scale: denominator must not be zero');
    return brand(divRoundHalfAwayFromZero(amount * n, d));
}
function toBigIntExact(value, label) {
    if (typeof value === 'bigint')
        return value;
    if (!Number.isSafeInteger(value)) {
        throw new TypeError(`${label} must be an integer, received ${value}`);
    }
    return BigInt(value);
}
/** Percent precision retained by {@link percentage}: 6 decimal places. */
const PCT_SCALE = 1_000_000;
/**
 * `pct` percent of `amount` — discounts (§3, §5.6), cashback / recharge bonus
 * (§5.5), tax-style surcharges, margin math.
 *
 * `percentage(fromIqd(50_000), 10)` -> 5,000 IQD.
 *
 * Fractional percentages are supported to 6 decimal places. The result is
 * rounded to the nearest millidinar (halves away from zero) — NOT to a whole
 * IQD. Whole-IQD rounding happens once, at close, via {@link applyRounding}.
 */
function percentage(amount, pct) {
    if (typeof pct !== 'number' || !Number.isFinite(pct)) {
        throw new TypeError(`percentage expects a finite number, received ${String(pct)}`);
    }
    const magnitude = Math.round(Math.abs(pct) * PCT_SCALE);
    if (!Number.isSafeInteger(magnitude)) {
        throw new RangeError(`percentage: ${pct}% exceeds the supported precision range`);
    }
    const scaledPct = BigInt(pct < 0 ? -magnitude : magnitude);
    return brand(divRoundHalfAwayFromZero(amount * scaledPct, 100n * BigInt(PCT_SCALE)));
}
/**
 * Hourly session billing (§4.1, §5.2): what `milliseconds` of play cost at
 * `rateMdPerHour`.
 *
 *     amount = rate * ms / 3_600_000
 *
 * One multiplication, one division, no intermediate rounding — full precision
 * is preserved down to the millidinar (0.001 IQD), which is three orders of
 * magnitude finer than the whole-IQD rounding applied at close. The rate must
 * be the snapshot copied onto the session row at start, never the live price.
 *
 * @throws RangeError for negative durations.
 */
function prorate(rateMdPerHour, milliseconds) {
    const ms = toBigIntExact(milliseconds, 'prorate milliseconds');
    if (ms < 0n)
        throw new RangeError(`prorate: duration must not be negative (${ms}ms)`);
    return brand(divRoundHalfAwayFromZero(rateMdPerHour * ms, exports.MS_PER_HOUR));
}
/** {@link prorate} over a `[start, end)` interval. */
function prorateBetween(rateMdPerHour, start, end) {
    return prorate(rateMdPerHour, end.getTime() - start.getTime());
}
/* -------------------------------------------------------------------------- */
/* Comparison                                                                 */
/* -------------------------------------------------------------------------- */
/** -1 | 0 | 1 — sortable comparator for tables and reports. */
function compare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function isZero(a) {
    return a === 0n;
}
function isNegative(a) {
    return a < 0n;
}
function isPositive(a) {
    return a > 0n;
}
function min(a, b) {
    return a <= b ? a : b;
}
function max(a, b) {
    return a >= b ? a : b;
}
/** Clamp to `>= 0` — e.g. a discount must never push a line item negative. */
function clampToZero(a) {
    return a < 0n ? exports.ZERO_MD : a;
}
/** All rounding rules, for settings dropdowns and validation. */
exports.ROUNDING_RULES = ['AVERAGE', 'UP', 'DOWN'];
function isRoundingRule(value) {
    return typeof value === 'string' && exports.ROUNDING_RULES.includes(value);
}
/**
 * Round using the venue's rule, to a **whole IQD** by default or to a coarser
 * cash step when one is given.
 *
 *  - `AVERAGE` — nearest step, halves away from zero (500 md -> 1 IQD).
 *  - `UP`      — ceiling, toward +Infinity.
 *  - `DOWN`    — floor, toward -Infinity.
 *
 * THE STEP IS WHAT MAKES CHANGE POSSIBLE AT A COUNTER. A till holding no coins
 * below 250 IQD cannot settle a bill of 2,220 however exact the arithmetic was,
 * so the venue sets a step and the grand total lands on it: 2,220 becomes 2,250
 * under AVERAGE, and 2,010 becomes 2,000. Per-session closes deliberately keep
 * the whole-dinar default — rounding each session to 250 and then summing would
 * charge the party several steps of nothing.
 *
 * Apply this exactly ONCE, when a session or bill closes. Rounding intermediate
 * line items and then summing produces different (and wrong) totals.
 *
 * Note for negative amounts (refunds, negative sales): `UP`/`DOWN` are defined
 * mathematically, so `UP` on -1,500 md yields -1,000 md (toward +Infinity), not
 * -2,000 md. `AVERAGE` is symmetric about zero.
 */
function applyRounding(amount, rule, stepMd = brand(exports.MD_PER_IQD)) {
    // A step of zero (or less) is "no coarse rounding configured" and means the
    // whole dinar this has always used. Guarded rather than trusted: a zero step
    // reaching the modulo below would divide by zero on every checkout.
    const step = stepMd > 0n ? stepMd : brand(exports.MD_PER_IQD);
    switch (rule) {
        case 'AVERAGE':
            return brand(roundToStep(amount, step));
        case 'UP':
            return brand(ceilToStep(amount, step));
        case 'DOWN':
            return brand(floorToStep(amount, step));
        default:
            throw new RangeError(`applyRounding: unknown rounding rule "${String(rule)}"`);
    }
}
/**
 * The venue's cash step in millidinars, from the setting in whole IQD.
 *
 * Zero means the setting is off, which the rounder reads as a whole dinar.
 */
function roundingStepMd(stepIqd) {
    return stepIqd > 0 ? brand(BigInt(Math.trunc(stepIqd)) * exports.MD_PER_IQD) : brand(0n);
}
/**
 * The ONE grand-total rounding pass at checkout. Used by the till and by the
 * drawer that quotes the customer, so the two cannot drift.
 *
 * A CASH STEP ALWAYS ROUNDS TO THE NEAREST, whatever `roundingRule` says, and
 * that asymmetry is deliberate rather than an oversight:
 *
 *  - `roundingRule` is a SUB-DINAR policy. At a whole dinar the most it can
 *    move a bill is 999 md, so "always down" is a rounding convention nobody
 *    argues about at a counter.
 *  - A cash step is a different size of decision. At 250 IQD, "always down"
 *    hands back up to 249 on every bill and "always up" takes up to 249; over a
 *    night either is real money moving one way for no reason anyone chose.
 *    Nearest is the only one that averages out.
 *
 * So the venue keeps rounding its sessions down to the dinar, and its totals
 * land on the nearest payable figure. With no step configured this is exactly
 * the pass it always was, rule and all.
 */
function roundCheckoutTotal(amount, rule, stepIqd) {
    const step = roundingStepMd(stepIqd);
    return step > 0n ? applyRounding(amount, 'AVERAGE', step) : applyRounding(amount, rule);
}
/** True when `amount` is an exact whole IQD (no sub-dinar remainder). */
function isWholeIqd(amount) {
    return amount % exports.MD_PER_IQD === 0n;
}
/**
 * Human-readable money: thousands separators, no decimals by default.
 *
 *     formatIqd(fromIqd(5000))                       // "5,000 IQD"
 *     formatIqd(fromIqd(8333), { locale: 'ar' })     // "8,333 IQD"
 *     formatIqd(x, { currencySymbol: 'د.ع' })        // "8,333 د.ع"
 *
 * Formatting is display-only; it never feeds back into billing math.
 */
function formatIqd(amount, options = {}) {
    const { currencySymbol = 'IQD', locale = 'en', decimals = 0, numberingSystem = 'latn', symbolPosition = 'suffix', separator = ' ', } = options;
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 3) {
        throw new RangeError('formatIqd: decimals must be an integer in 0..3');
    }
    const decimalString = toIqdDecimalString(amount, 3);
    const formatted = formatDecimalString(decimalString, locale, decimals, numberingSystem);
    if (symbolPosition === 'none' || currencySymbol === '')
        return formatted;
    return symbolPosition === 'prefix'
        ? `${currencySymbol}${separator}${formatted}`
        : `${formatted}${separator}${currencySymbol}`;
}
function formatDecimalString(decimalString, locale, decimals, numberingSystem) {
    const intlOptions = {
        style: 'decimal',
        useGrouping: true,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        ...(numberingSystem === 'latn' ? { numberingSystem: 'latn' } : {}),
    };
    try {
        // Intl.NumberFormat accepts an exact decimal string (Intl.NumberFormat v3),
        // so huge bigints and sub-dinar fractions survive formatting without a float
        // round-trip. TypeScript's ES2023 lib still types `format` as number-only.
        return new Intl.NumberFormat(locale, intlOptions).format(decimalString);
    }
    catch {
        // Unknown/unsupported locale tag: fall back to English rather than throwing
        // inside a receipt render.
        return new Intl.NumberFormat('en', intlOptions).format(decimalString);
    }
}
//# sourceMappingURL=money.js.map